<!--
 * @Author:
 * @LastEditTime: 2025-12-11 17:30:30
 * @Description:
-->
<template>
  <div class="w-full h-full relative" v-loading="loadingEngine">
    <img
      id="playerEngineId"
      class="engineVideo w-full"
      v-if="modelSetting.cloudRenderLoadMode === 'img'"
    />
    <video
      id="playerEngineId"
      autoplay
      playsinline
      muted
      class="h-full w-full object-fill"
      v-else
    ></video>
    <!-- 模型进度条 -->
    <player-progress
      :loadType="loadType"
      :percentage="loadPercentage"
      v-if="showLoadProgress"
    ></player-progress>
  </div>
</template>

<script>
import { round, isEmpty, forEach, last, get } from 'lodash'
import { loadSceneFromJson, isJSONObj, getNodesByNodeType } from '@/assets/js/engineerSceneData'
import downloadFile from '@/assets/js/modelDownload'

const PlayerProgress = () => {
  return import('./PlayerProgress')
}
export default {
  name: 'ModelViewVulkan',
  components: { PlayerProgress },
  props: {
    cloudRender: {
      type: Object,
      default: () => {},
    },
    sceneOptions: {
      type: Object,
      defalut: () => {
        return {
          isEnableGis: false,
          latitude: 0,
          longitude: 0,
          name: '',
          id: '',
          folderId: '',
          jsonData: '',
        }
      },
    },
  },
  data() {
    return {
      player: null, // player实例
      loadingEngine: false, // 引擎加载状态
      showLoadProgress: false, // 进度条显隐
      loadType: '', // 进度条类型
      loadPercentage: 0, // 进度条值
      sceneFileList: [],
      fileCount: 0,
      sceneData: {},
      sceneRootNode: 0,
      initStatus: false,
      modelEngineId: '',
      preloadEngine: null,
      // 单位矩阵
      mat: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
    }
  },
  computed: {
    // 模型相关设置
    modelSetting() {
      return (
        get(Wiz, 'globalSettings.globalConfig.modelSetting') ||
        get(Wiz, 'globalSettings.dynamicModules.modules["electronic-fence"].config') ||
        {}
      )
    },
    // 云渲染协议
    cloudProtocol() {
      return this.modelSetting.cloudProtocol || 'ws'
    },
    // 云渲染服务路径
    cloudServerPath() {
      return this.modelSetting.cloudServerPathVulkan || 'renderapi.wiz.top:7082'
    },
  },
  watch: {
    sceneOptions: {
      async handler(data) {
        if (this.initStatus) {
          await this.switchScene()
          if (data.isEnableGis && data.longitude && data.latitude) {
            await this.cloudRender.GIS.loadGis(this.modelSetting.globalMapPath)
            this.moveToLLH(data.longitude, data.latitude)
          } else {
            await this.cloudRender.GIS.unLoadGis()
          }
        }
      },
      deep: true,
    },
    async fileCount(num) {
      if (num === 0) {
        return
      }
      if (num >= this.sceneFileList.length) {
        console.log('场景文件加载完成', this.sceneData, this.sceneRootNode, this.player)
        await loadSceneFromJson(this.sceneData, this.sceneRootNode, this.player)
        if (this.sceneData.cameraParams && isJSONObj(this.sceneData.cameraParams)) {
          const { eye, target, up } = JSON.parse(this.sceneData.cameraParams)
          await this.player.Native.Camera.moveTo(eye, target, up, 2)
        } else if (this.sceneOptions.isEnableGis) {
          await this.moveToLLH(this.sceneOptions.longitude, this.sceneOptions.latitude)
        } else {
          await this.locationScene()
        }
        this.$emit('loadSceneEnd')
        this.fileCount = 0
      } else {
        this.checkTextureFile(this.sceneFileList[num])
      }
    },
  },
  mounted() {
    this.loadingEngine = true
    this.runPlayer()
    window.addEventListener('beforeunload', async (e) => {
      if (this.player) {
        this.cloudRender.Engine.destroyEngine()
      }
    })
  },
  beforeDestroy() {
    if (this.player) {
      this.player.exit()
    }
  },
  methods: {
    // 加载player引擎
    async runPlayer(engineId) {
      this.preloadEngine = engineId || null
      this.modelEngineId = this.generateGUID()
      const { data } = await this.cloudRender.Engine.loadEngine({
        signalServer: `${this.cloudProtocol}://${this.cloudServerPath}`,
        options: {
          openDebug: true,
          videoDom: document.getElementById('playerEngineId'), // 绑定视频标签
          userTimeout: 1000 * 60 * 60, // 用户操作超时设置，毫秒
          exitTimeout: 1000 * 60 * 1, // 用户超时后多久断开会话
          maxTimeout: 1000, // 会话超时时间，允许掉线后引擎保存多久，在该时间内可重新连接到会话，否则会创建新的会话
          engineId: engineId || this.modelEngineId,
        },
      })
      if (data) {
        this.player = data.getDataInfo()
        this.cloudRender.Dispatch.on('Engine', async (e) => {
          if (e.type === 'UserTimeOut') {
            // 用户操作超时
            console.error('用户操作超时')
          }
          if (e.type === 'TimeOutExit') {
            // 断开会话
            console.error('超时断开')
          }
        })
        // 实例化完成将实例化后的player传出去
        this.$emit('sendPlayer', this.player)
        window.player = this.player
        this.loadingEngine = false
        this.initPlayer(engineId)
      }
    },
    generateGUID() {
      let d = new Date().getTime()
      if (window.performance && typeof window.performance.now === 'function') {
        d += performance.now()
      }
      const guid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (d + Math.random() * 16) % 16 | 0
        d = Math.floor(d / 16)
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
      })
      return guid
    },
    // 移动相机到经纬度
    async moveToLLH(long, lat) {
      if (long && lat) {
        const elevation = (await this.player.Native.GisPlatform.queryDEM([[long, lat]]))[0]
        this.player.Native.Camera.addMoveToLLH([long, lat, 20000], 0)
        this.player.Native.Camera.addMoveToLLH([long, lat, elevation + 800], 8)
      }
    },
    // 初始化player
    async initPlayer(engineId) {
      // 设置点选时其他节点半透明
      this.player.Native.Setting.set('color.pickup.bkAlpha', 'float', '0.5')
      this.player.Native.Tools.EventBrowseLClick.connect((e) => {
        this.$emit('modelClick', e)
      })
      this.loadingEngine = false
      const settingsFileUrl = `data://../apps/${this.modelSetting.folderName}/settings.json`
      const settingsData = await this.player.Native.File.loadText(settingsFileUrl)
      if (settingsData) {
        const defaultSettings = JSON.parse(settingsData)
        // forEach(defaultSettings.Show, (item) => {
        //   this.player.Native.Settings.set(item.name, item.type, item.value)
        // })
        await this.cloudRender.Setting.set(defaultSettings.Show)
      }
      // 获取到经纬度添加地图
      await this.loadScene()
      if (this.sceneOptions.isEnableGis) {
        await this.cloudRender.GIS.loadGis(this.modelSetting.globalMapPath)
        this.moveToLLH(this.sceneOptions.longitude, this.sceneOptions.latitude)
      }
      this.initStatus = true
    },
    // 检查文件是否存在
    async checkTextureFile(data) {
      if (data && data.code && data.code.modelFileId) {
        if (data.code.materialFileId && data.code.materialFileId !== 'undefined') {
          const url = `data://../apps/${data.code.filePath.split('/')[4]}/models/${
            data.code.modelFileId
          }/${data.code.modelFileId}.zip`
          const fileInfo = {
            id: data.code.materialFileId,
            url,
            fileType: 'zip',
          }
          const apiConfig = {
            path: `${
              Wiz.GetApiClientInstance('file-management')?.basePath
            }/api/file-management/files/${data.code.materialFileId}/download`,
            method: 'GET',
            headers: {
              Authorization: `Bearer ${Wiz.AuthHooks.GetToken()}`,
            },
          }
          const res = await downloadFile(
            this.player,
            fileInfo,
            apiConfig,
            this.progressMsg,
            this.downloadMaterialMsg(data)
          )
          if (res.type === 0 && res.success) {
            this.checkModelFile(data)
          } else {
            this.showLoadProgress = true
            this.loadType = '下载中...'
          }
        } else {
          this.checkModelFile(data)
        }
      }
    },
    // 检查模型是否存在
    async checkModelFile(data) {
      const fileType = last(data.code.filePath.split('.')) || 'pr'
      const url = data.code.filePath
      const fileInfo = {
        id: data.code.modelFileId,
        url,
        fileType,
      }
      const apiConfig = {
        path: `${Wiz.GetApiClientInstance('file-management')?.basePath}/api/file-management/files/${
          data.code.modelFileId
        }/download`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${Wiz.AuthHooks.GetToken()}`,
        },
      }
      const res = await downloadFile(
        this.player,
        fileInfo,
        apiConfig,
        this.progressMsg,
        this.downloadMsg
      )
      if (res.type === 0 && res.success) {
        // 有模型就加载模型
        this.fileCount += 1
      } else {
        this.showLoadProgress = true
        this.loadType = '下载中...'
      }
    },
    // 监听下载模型是否成功
    downloadMsg(file, err) {
      this.loadPercentage = 0
      this.showLoadProgress = false
      if (err) {
        this.$message.warning(`下载文件失败，${file}, ${err}`)
      }
      this.fileCount += 1
    },

    // 监听下载模型是否成功
    downloadMaterialMsg(data) {
      const that = this
      const fun = async (file, err) => {
        that.showLoadProgress = false
        that.loadPercentage = 0
        if (err) {
          that.$message.warning(`下载贴图文件失败，${file}, ${err}`)
        }
        setTimeout(() => {
          that.checkModelFile(data)
        }, 1500)
      }
      return fun
    },

    // 监听下载进度
    progressMsg(file, bytesReceived, bytesTotal) {
      if (bytesTotal) {
        this.loadPercentage = (bytesReceived / bytesTotal) * 100
      }
    },
    // 加载场景
    async loadScene() {
      this.sceneRootNode = await this.player.Native.Node.getRoot()
      if (this.sceneOptions.jsonData && isJSONObj(this.sceneOptions.jsonData)) {
        this.sceneData = JSON.parse(this.sceneOptions.jsonData)
        this.sceneFileList = await getNodesByNodeType(this.sceneData, [])
        if (!isEmpty(this.sceneFileList)) {
          this.checkTextureFile(this.sceneFileList[0])
        } else {
          await loadSceneFromJson(
            this.sceneData,
            this.sceneRootNode,
            this.player,
            this.sceneOptions,
            []
          )
          if (this.sceneData.cameraParams && isJSONObj(this.sceneData.cameraParams)) {
            const { eye, target, up } = JSON.parse(this.sceneData.cameraParams)
            await this.player.Native.Camera.moveTo(eye, target, up, 2)
          } else if (this.sceneOptions.isEnableGis) {
            await this.moveToLLH(this.sceneOptions.longitude, this.sceneOptions.latitude)
          }
        }
      } else {
        console.log('场景数据为空')
        const node = await this.player.Native.Node.loadFile('data://../data/models/sampler(1).pprj')
        const root = await this.player.Native.Node.getRoot()
        await this.player.Native.Node.setProperty(node, {
          parent: root,
          name: 'cs',
        })
        await this.player.Native.Node.addToFactory(node)
        await this.player.Native.Camera.moveToNode(node, 2)
        this.$emit('loadSceneEnd')
        // const nodePtr = await this.player.Native.NodeProxy.createNode(
        //   this.sceneRootNode,
        //   this.sceneOptions.name
        // )
        // await this.player.Native.NodeProxy.setNodeInfo(nodePtr, {
        //   code: JSON.stringify({
        //     type: 'Root',
        //     fileType: '',
        //     filePath: '',
        //     scene: this.sceneOptions.id,
        //   }),
        // })
      }
    },
    // 切换场景
    async switchScene() {
      const rootNode = await this.player.Native.Node.getRoot()
      const childrenNode = await this.player.Native.Node.getProperty(rootNode, ['children'])
      console.log('childrenNode', childrenNode)
      await this.player.Native.Node.destroy(
        childrenNode.children.map((item) => {
          return item.node
        })
      )
      await this.loadScene()
    },
    // 移动视角
    async locationScene() {
      const nodeAabb = await this.player.Native.Node.getProperty(this.sceneRootNode, ['aabb'])
      await this.player.Native.Camera.moveToAabb(nodeAabb.aabb, 1)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .el-loading-mask {
  background-color: transparent !important;
}
</style>
